import { useEffect, useState } from "react";
import { motion } from "framer-motion";

interface ScoreGaugeProps {
  score: number; // 0..100
}

function colorBand(score: number) {
  if (score >= 80) return { start: "#22c55e", end: "#10b981", text: "text-emerald-500" }; // verde
  if (score >= 60) return { start: "#eab308", end: "#f59e0b", text: "text-amber-500" };    // amarelo
  if (score >= 40) return { start: "#f97316", end: "#ef4444", text: "text-orange-500" };   // laranja
  return { start: "#dc2626", end: "#7f1d1d", text: "text-red-600" };                        // vermelho
}

export const ScoreGauge = ({ score }: ScoreGaugeProps) => {
  const [displayScore, setDisplayScore] = useState(0);
  const band = colorBand(score);

  useEffect(() => {
    setDisplayScore(0);
    const t = setTimeout(() => setDisplayScore(score), 150);
    return () => clearTimeout(t);
  }, [score]);

  // 0..100 -> -90..90
  const rotation = (Math.min(Math.max(displayScore, 0), 100) / 100) * 180 - 90;

  return (
    <div className="relative w-48 h-24 mx-auto my-6">
      {/* arco */}
      <svg className="w-full h-full" viewBox="0 0 200 100">
        <defs>
          <linearGradient id="score-gauge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={band.start} />
            <stop offset="100%" stopColor={band.end} />
          </linearGradient>
        </defs>

        {/* fundo */}
        <path
          d="M 20 80 A 80 80 0 0 1 180 80"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="12"
          strokeLinecap="round"
        />

        {/* progresso */}
        <motion.path
          d="M 20 80 A 80 80 0 0 1 180 80"
          fill="none"
          strokeWidth="12"
          strokeLinecap="round"
          stroke="url(#score-gauge-gradient)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: Math.max(0, Math.min(score, 100)) / 100 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
      </svg>

      {/* agulha — voltando ao posicionamento absoluto e com top:0 */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 origin-bottom"
        initial={{ rotate: -90 }}
        animate={{ rotate: rotation }}
        transition={{ duration: 1.1, ease: "easeOut" }}
        // altura da agulha calibrada para que a base coincida com o centro do arco (~70% da altura)
        style={{ height: "80px" }}
      >
        <div className="w-1 h-full bg-foreground rounded-full shadow-lg" />
        <div className="mx-auto mt-[-6px] w-2 h-2 rounded-full bg-foreground" />
      </motion.div>

      {/* centro (eixo) — mesma posição que você usava antes */}
      <div
        className="absolute top-[70%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full shadow-lg"
        style={{ background: band.end, margin: 5 }}
      />

      {/* número — agora também muda a cor conforme a faixa */}
      <motion.div
        className={`absolute top-[85%] left-1/2 w-12 -translate-x-1/2 text-center text-3xl font-bold ${band.text}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        {Math.round(displayScore)}
      </motion.div>

    </div>
  );
};
